import { z } from 'zod';
import { AnalyticsEventInputSchema, type AnalyticsEventInput } from '../contracts/analytics';
import type { RequestFn } from './client';

/**
 * Namespace `client.analytics` — транспорт для `POST /api/analytics/events`
 * (ADR-0084, PR#1 задача 0.2). **ТОЛЬКО транспорт.**
 *
 * **Out of scope (явно):**
 *   - Гейт согласия `marketingAnalytics` + fail-safe — Фаза 2 (клиент-эмиттер в
 *     `apps/web`, читает `authStore`). Этот метод НЕ проверяет согласие — вызывать
 *     его должен только эмиттер за гейтом.
 *   - Сам write-API эндпоинт + таблица событий D1 — задача 1.3.
 *
 * Батч-массив `AnalyticsEventInput[]` (§1a — БЕЗ серверных полей; `userId`/`region`/
 * `ts` проставит сервер из JWT). Идемпотентность по `eventId` — на сервере (1.3).
 * Относительный путь (P-013), auth по JWT (события привязаны к userId из токена).
 *
 * P-009 защита уровня 2: client-side `safeParse` всего батча ДО сети — невалидный
 * payload (например feature вне реестра, спуф серверных полей) отклоняется без
 * сетевого вызова. Симметрия с `feedback.create()` / `onboarding.register()`.
 *
 * Response 1.3 ещё не заморожен (P-011) → провизорная `partial`-схема:
 * `{ accepted? }` толерантна и к `{}`, и к `{ accepted: N }`. Финализирует задача 1.3.
 */
export const AnalyticsPostEventsResponseSchema = z
  .object({
    accepted: z.number().int().nonnegative(),
  })
  .partial();
export type AnalyticsPostEventsResponse = z.infer<typeof AnalyticsPostEventsResponseSchema>;

export interface AnalyticsApi {
  /**
   * POST /api/analytics/events — отправить батч событий (транспорт-only).
   *
   * @param events — `AnalyticsEventInput[]` (Zod-валидирован client-side через safeParse).
   * @returns `AnalyticsPostEventsResponse` (провизорно `{ accepted? }`, финал — 1.3).
   * @throws `z.ZodError` если client-side валидация не прошла (нет сетевого вызова).
   * @throws `ApiClientError` при 401 / 400 / network / 5xx / PARSE_ERROR.
   */
  postEvents(events: AnalyticsEventInput[]): Promise<AnalyticsPostEventsResponse>;
}

export function createAnalyticsApi(request: RequestFn): AnalyticsApi {
  return {
    postEvents: (events) => {
      const parsed = z.array(AnalyticsEventInputSchema).min(1).safeParse(events);
      if (!parsed.success) {
        return Promise.reject(parsed.error);
      }
      return request({
        method: 'POST',
        path: '/api/analytics/events',
        body: parsed.data,
        responseSchema: AnalyticsPostEventsResponseSchema,
        auth: true,
      });
    },
  };
}
